import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loader2, Info, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { useMCPConnection } from "./hooks/useMCPConnection";

interface AddCustomMCPFormProps {
  onClose: () => void;
}

export default function AddCustomMCPForm({ onClose }: AddCustomMCPFormProps) {
  const [serverName, setServerName] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [transportType, setTransportType] = useState<"http" | "sse">("http");

  const { isConnecting, connectToServer } = useMCPConnection({
    onSuccess: onClose,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serverName.trim() || !serverUrl.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const url = new URL(serverUrl);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        toast.error("Only HTTP and HTTPS URLs are supported");
        return;
      }
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    await connectToServer(serverUrl, serverName, transportType);
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-muted/30 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Add Custom MCP Server
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Connect to your own HTTP-based MCP server
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          disabled={isConnecting}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="serverName" className="text-xs">
              Server Name
            </Label>
            <Input
              id="serverName"
              placeholder="e.g., My Custom Server"
              value={serverName}
              onChange={(e) => setServerName(e.target.value)}
              disabled={isConnecting}
              className="h-9 text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="serverUrl" className="text-xs">
              Server URL
            </Label>
            <Input
              id="serverUrl"
              placeholder="e.g., http://localhost:3000/mcp"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              disabled={isConnecting}
              className="h-9 text-xs"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="transportType" className="text-xs">
              Transport Type
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="max-w-[280px] text-xs">
                <p className="mb-2">
                  <strong>HTTP:</strong> Modern streamable HTTP transport
                  (recommended for most servers)
                </p>
                <p>
                  <strong>SSE:</strong> Legacy Server-Sent Events transport (for
                  older MCP servers)
                </p>
                <p className="mt-2">
                  Check your server's documentation to determine which transport
                  it uses.
                </p>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={transportType}
              onValueChange={(value) =>
                setTransportType(value as "http" | "sse")
              }
              disabled={isConnecting}
            >
              <SelectTrigger className="h-9 w-[180px] text-xs">
                <SelectValue placeholder="Select transport" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="http" className="text-xs">
                  HTTP (Modern)
                </SelectItem>
                <SelectItem value="sse" className="text-xs">
                  SSE (Legacy)
                </SelectItem>
              </SelectContent>
            </Select>
            <a
              href="https://modelcontextprotocol.io/docs/concepts/transports"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>Learn about MCP transports</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isConnecting}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isConnecting}
            size="sm"
          >
            {isConnecting && (
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
            )}
            Connect
          </Button>
        </div>
      </form>
    </div>
  );
}
